<template>
    <div class="container">
        <section v-if="lastScannedProduct" class="recent-products">
            <h2 class="recent-products__title title">Last scan</h2>

            <div class="recent-products__wrap">
                <RouterLink :to="{ name: 'product-info' }" class="recent-product">
                    <div class="recent-product__info">
                        <div class="recent-product__info-wrap">
                            <div class="recent-product__meta">
                                {{ lastScannedProduct.date }}
                            </div>
                            <div class="recent-product__name">
                                {{ lastScannedProduct.name }}
                                <br />
                                {{ lastScannedProduct.year }}
                            </div>
                        </div>
                        <div class="recent-product__link">More details</div>
                    </div>
                    <div class="recent-product__img-wrap">
                        <img
                            :src="lastScannedProduct.product_image?.url || productPlaceholder"
                            alt=""
                            class="recent-product__img"
                        />
                    </div>
                </RouterLink>
                <RouterLink to="/scan" class="add-product-card">
                    <div class="add-product-card__icon">
                        <PlusIcon />
                    </div>
                    <div class="add-product-card__text">Scan again</div>
                </RouterLink>
            </div>
        </section>

        <section v-else class="how-it-works">
            <div class="how-it-works__block how-it-works__block_head">
                <h2 class="how-it-works__title title title_big">How it works</h2>
                <p class="how-it-works__description">
                    Scan the label to get more information about your product
                </p>
                <img class="how-it-works__img" src="@/assets/images/how_it_works.png" />
            </div>
            <div class="how-it-works__block how-it-works__block_btn">
                <RouterLink to="/scan" class="how-it-works__btn btn"> Scan to verify </RouterLink>
                <RouterLink to="/register" class="how-it-works__btn btn btn_empty">
                    Scan to register
                </RouterLink>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import productPlaceholder from '@/assets/images/product_placeholder.png'
import PlusIcon from '@/components/icons/plusIcon.vue'
import { normalizeLastScannedProduct } from '@/services/api'
import { LastScannedProduct } from '@/types'
import { formatDateEn } from '@/utils/dates'
import { computed } from 'vue'

const LAST_SCAN_KEY = 'last_scan'

type RecentProduct = {
    name: string
    bg_image?: { url?: string }
    product_image?: { url?: string }
    date?: string
    year?: string
}

const lastScannedProduct = computed<RecentProduct | null>(() => {
    const stored = localStorage.getItem(LAST_SCAN_KEY)
    if (!stored) return null
    try {
        const scan = normalizeLastScannedProduct(JSON.parse(stored) as LastScannedProduct)
        if (!scan?.product) return null
        return {
            name: scan.product.name,
            bg_image: scan.product.bg_image ? { url: scan.product.bg_image.url } : undefined,
            product_image: scan.product.product_image
                ? { url: scan.product.product_image.url }
                : undefined,
            date: scan.scanned_at ? formatDateEn(scan.scanned_at) : '',
            year: scan.product.year ? String(scan.product.year) : '',
        }
    } catch {
        return null
    }
})
</script>

<style scoped lang="stylus">
.container
  height 100%
  padding 16px

.recent-products
  &__title
    margin-bottom 20px
  &__wrap
    display flex
    gap 12px

.add-product-card
    flex-shrink 0
    width 120px
    height 230px
    background-color var(--app-bg-secondary)
    border-radius 12px
    padding 12px
    display flex
    align-items center
    flex-direction column
    justify-content center
    gap 12px
    text-decoration none
    &__icon
        width 24px
        height 24px
    &__text
        font-size 14px
        line-height 18px
        font-weight 500
        color var(--app-text-grey-color)
        text-align center

.recent-product
    width 100%
    height 230px
    background-color var(--app-bg-secondary)
    border-radius 12px
    padding 12px 0 12px 12px
    display flex
    align-items stretch
    gap 12px
    text-decoration none
    &__link
        font-size 16px
        line-height 19px
        font-weight 500
        color var(--app-main-accent-color)
        text-decoration none
    &__info
        width 100%
        display flex
        flex-direction column
        justify-content space-between
    &__meta
        margin 4px 0
        font-size 10px
        line-height 12px
        font-weight 400
        color var(--app-text-grey-color)
    &__name
        display block
        display -webkit-box
        -webkit-line-clamp 5
        -webkit-box-orient vertical
        overflow hidden
        text-overflow ellipsis
        max-width 100%
        max-height 95px
        font-size 16px
        line-height 19px
        font-weight 500
        color var(--app-text-color-secondary)
    &__img
        object-fit contain
        width 100%
        height 100%
        &-wrap
            flex-shrink 0
            width 80px
            height 100%

.how-it-works
    display flex
    flex-direction column
    align-items center
    justify-content space-between
    gap 16px
    width 100%
    height 100%
    padding 8px 0 0
    &__img
        flex-shrink 0
        display block
        width 100%
        max-width 400px
        height 40vh
        object-fit contain
    &__title
        text-align center
    &__description
        text-align center
        font-size 16px
        line-height 22px
        font-weight 400
        color var(--app-text-color-secondary)
    &__block
        width 100%
        display flex
        flex-direction column
        align-items center
        &_head
            gap 30px
        &_btn
            gap 10px
    &__btn
        width 100%
</style>
